import React, { useEffect, useState, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import axios from "axios";
import { getServices } from "../API/servicesApi";
import { getCategories } from "../API/categoriesApi";

const API = "https://api.allianceprotravaux.be/api";

/* ── tiny helpers ── */
const pct = (used, total) => total > 0 ? Math.round((used / total) * 100) : 0;

const gauge = (value) => {
  if (value < 50) return "#22c55e";
  if (value < 75) return "#f59e0b";
  return "#ef4444";
};

/* ── Radial gauge SVG ── */
function RadialGauge({ value, label, color }) {
  const r = 44, cx = 56, cy = 56;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <svg width={112} height={80} viewBox="0 0 112 80">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1e293b" strokeWidth={10} strokeDasharray={`${circ * 0.75} ${circ}`} strokeDashoffset={circ * 0.125} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={`${dash * 0.75} ${circ}`}
          strokeDashoffset={circ * 0.125}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
        <text x={cx} y={cy + 6} textAnchor="middle" fill="#f1f5f9" fontSize={15} fontWeight={700}>{value}%</text>
      </svg>
      <span style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
    </div>
  );
}

/* ── Stat card ── */
function StatCard({ label, value, icon, accent, sub }) {
  return (
    <div style={{
      background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
      border: `1px solid ${accent}33`,
      borderRadius: 16,
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: -20, right: -20,
        width: 80, height: 80, borderRadius: "50%",
        background: `${accent}18`,
      }} />
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: "#f1f5f9", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: accent, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

/* ── Progress bar ── */
function ProgressBar({ label, used, total, unit }) {
  const p = pct(used, total);
  const c = gauge(p);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>{label}</span>
        <span style={{ fontSize: 12, color: c, fontWeight: 700 }}>{used} / {total} {unit} ({p}%)</span>
      </div>
      <div style={{ height: 6, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${p}%`, background: c,
          borderRadius: 4, transition: "width 0.8s ease"
        }} />
      </div>
    </div>
  );
}

/* ── Generate sparkline history (simulated rolling window) ── */
const makeHistory = (current, points = 12) => {
  const arr = [];
  const now = Date.now();
  for (let i = points - 1; i >= 0; i--) {
    const t = new Date(now - i * 5000);
    arr.push({
      time: t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
      value: Math.max(0, Math.min(100, current + (Math.random() - 0.5) * 20)),
    });
  }
  return arr;
};

const COLORS = ["#3b82f6", "#8b5cf6", "#06b6d4", "#f59e0b", "#10b981"];

export default function Dashboard({ search }) {
  const [stats, setStats] = useState({ categories: 0, services: 0, types: 0, contacts: 0 });
  const [server, setServer] = useState(null);
  const [cpuHistory, setCpuHistory] = useState([]);
  const [ramHistory, setRamHistory] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  /* ── fetch API data ── */
  const fetchApiData = useCallback(async () => {
    try {
      const [catRes, svcRes, contactRes] = await Promise.allSettled([
        axios.get(`${API}/categories`),
        getServices(),
        axios.get(`${API}/contacts`),
      ]);

      const categories = catRes.status === "fulfilled"
        ? (catRes.value.data?.data ?? catRes.value.data ?? []) : [];
      const services = svcRes.status === "fulfilled"
        ? (svcRes.value.data?.data ?? svcRes.value.data ?? []) : [];
      const contacts = contactRes.status === "fulfilled"
        ? (contactRes.value.data?.data ?? contactRes.value.data ?? []) : [];

      const totalTypes = services.reduce((a, s) => a + (s.types?.length || 0), 0);

      setStats({
        categories: categories.length,
        services: services.length,
        types: totalTypes,
        contacts: contacts.length,
      });

      // pie data: services per category
      const map = {};
      categories.forEach(c => { map[c.id] = { name: c.name, value: 0 }; });
      services.forEach(s => { if (map[s.category_id]) map[s.category_id].value++; });
      setCategoryData(Object.values(map).filter(d => d.value > 0));

    } catch (err) {
      console.error("API data error:", err);
    }
  }, []);

  /* ── fetch server stats ── */
  const fetchServerStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/server-stats`);
      const d = res.data;
      setServer(d);
      setLastUpdated(new Date());

      setCpuHistory(prev => {
        const next = [...prev, {
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
          value: d.cpu ?? 0,
        }];
        return next.slice(-20);
      });

      setRamHistory(prev => {
        const ramPct = pct(d.ram?.used, d.ram?.total);
        const next = [...prev, {
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
          value: ramPct,
        }];
        return next.slice(-20);
      });

    } catch (err) {
      // server-stats not available yet — use mock
      const mockCpu = Math.round(20 + Math.random() * 40);
      const mockRam = { total: 2048, used: Math.round(800 + Math.random() * 400), free: 0 };
      mockRam.free = mockRam.total - mockRam.used;
      const mockDisk = { total: 20, used: 8.4, free: 11.6 };
      setServer({ cpu: mockCpu, ram: mockRam, disk: mockDisk });
      setLastUpdated(new Date());

      setCpuHistory(prev => {
        const next = [...prev, {
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
          value: mockCpu,
        }];
        return next.slice(-20);
      });
      setRamHistory(prev => {
        const rp = pct(mockRam.used, mockRam.total);
        const next = [...prev, {
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
          value: rp,
        }];
        return next.slice(-20);
      });
    }
  }, []);

  /* ── init ── */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchApiData(), fetchServerStats()]);
      setLoading(false);
    };
    init();
    const interval = setInterval(fetchServerStats, 10000); // refresh server every 10s
    return () => clearInterval(interval);
  }, [fetchApiData, fetchServerStats]);

  if (loading) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: 400, flexDirection: "column", gap: 16,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          border: "3px solid #1e293b", borderTop: "3px solid #3b82f6",
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ color: "#64748b", fontSize: 14 }}>Chargement du tableau de bord...</span>
      </div>
    );
  }

  const cpuPct = server?.cpu ?? 0;
  const ramPct = pct(server?.ram?.used, server?.ram?.total);
  const diskPct = pct(server?.disk?.used, server?.disk?.total);

  return (
    <div style={{
      padding: "0 0 40px 0",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      color: "#f1f5f9",
      minHeight: "100vh",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .dash-grid { display: grid; gap: 16px; }
        .recharts-tooltip-wrapper .recharts-default-tooltip {
          background: #0f172a !important;
          border: 1px solid #334155 !important;
          border-radius: 8px !important;
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#f1f5f9" }}>
            Tableau de bord
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#0d3874" }}>
            {lastUpdated ? `Mis à jour : ${lastUpdated.toLocaleTimeString()}` : "Chargement..."}
          </p>
        </div>
        <button
          onClick={() => { fetchApiData(); fetchServerStats(); }}
          style={{
            background: "#1e293b", border: "1px solid #334155",
            color: "#94a3b8", padding: "8px 16px", borderRadius: 10,
            cursor: "pointer", fontSize: 13, fontWeight: 600,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          ↻ Actualiser
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        <StatCard label="Catégories"  value={stats.categories} icon="🗂️" accent="#3b82f6" sub="Groupes de services" />
        <StatCard label="Services"    value={stats.services}   icon="🔧" accent="#8b5cf6" sub="Travaux enregistrés" />
        <StatCard label="Types"       value={stats.types}      icon="📋" accent="#06b6d4" sub="Sous-types au total" />
        <StatCard label="Messages"    value={stats.contacts}   icon="✉️" accent="#f59e0b" sub="Contacts reçus" />
      </div>

      {/* Server gauges + progress bars */}
      <div style={{
        display: "grid", gridTemplateColumns: "auto 1fr",
        gap: 24, background: "linear-gradient(135deg,#0f172a,#1e293b)",
        border: "1px solid #1e293b", borderRadius: 16, padding: 24, marginBottom: 24,
        alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 16, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Serveur — Live
          </div>
          <div style={{ display: "flex", gap: 24 }}>
            <RadialGauge value={cpuPct}  label="CPU"  color={gauge(cpuPct)}  />
            <RadialGauge value={ramPct}  label="RAM"  color={gauge(ramPct)}  />
            <RadialGauge value={diskPct} label="Disk" color={gauge(diskPct)} />
          </div>
        </div>
        <div style={{ borderLeft: "1px solid #1e293b", paddingLeft: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 16, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Détail
          </div>
          <ProgressBar label="CPU"  used={cpuPct}             total={100}                  unit="%" />
          <ProgressBar label="RAM"  used={server?.ram?.used}  total={server?.ram?.total}   unit="MB" />
          <ProgressBar label="Disk" used={server?.disk?.used} total={server?.disk?.total}  unit="GB" />
        </div>
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>

        {/* CPU history */}
        <div style={{
          background: "linear-gradient(135deg,#0f172a,#1e293b)",
          border: "1px solid #1e293b", borderRadius: 16, padding: 20,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            CPU — Historique
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={cpuHistory}>
              <defs>
                <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="time" tick={{ fontSize: 9, fill: "#475569" }} interval="preserveStartEnd" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#475569" }} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }} />
              <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="url(#cpuGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* RAM history */}
        <div style={{
          background: "linear-gradient(135deg,#0f172a,#1e293b)",
          border: "1px solid #1e293b", borderRadius: 16, padding: 20,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            RAM — Historique
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={ramHistory}>
              <defs>
                <linearGradient id="ramGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="time" tick={{ fontSize: 9, fill: "#475569" }} interval="preserveStartEnd" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#475569" }} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }} />
              <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="url(#ramGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Services per category pie */}
        <div style={{
          background: "linear-gradient(135deg,#0f172a,#1e293b)",
          border: "1px solid #1e293b", borderRadius: 16, padding: 20,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Services / Catégorie
          </div>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={categoryData} cx="50%" cy="50%" innerRadius={35} outerRadius={60}
                  dataKey="value" nameKey="name" paddingAngle={3}>
                  {categoryData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 13 }}>
              Aucune donnée
            </div>
          )}
          {/* Legend */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 8 }}>
            {categoryData.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#94a3b8" }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                {d.name} ({d.value})
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Services bar chart */}
      <div style={{
        background: "linear-gradient(135deg,#0f172a,#1e293b)",
        border: "1px solid #1e293b", borderRadius: 16, padding: 24, marginBottom: 24,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Répartition des services par catégorie
        </div>
        {categoryData.length > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={categoryData} barSize={32}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" name="Services" radius={[6, 6, 0, 0]}>
                {categoryData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 13 }}>
            Aucune donnée
          </div>
        )}
      </div>

      {/* Disk info cards */}
      {server?.disk && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
          {[
            { label: "Espace total",      value: `${server.disk.total} GB`, color: "#3b82f6" },
            { label: "Espace utilisé",    value: `${server.disk.used} GB`,  color: "#ef4444" },
            { label: "Espace disponible", value: `${server.disk.free} GB`,  color: "#22c55e" },
          ].map((item, i) => (
            <div key={i} style={{
              background: "linear-gradient(135deg,#0f172a,#1e293b)",
              border: `1px solid ${item.color}33`,
              borderRadius: 16, padding: "16px 20px",
              display: "flex", flexDirection: "column", gap: 4,
            }}>
              <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>{item.label}</span>
              <span style={{ fontSize: 26, fontWeight: 800, color: item.color }}>{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}